/**
 * Sales import from .xlsx / .csv / .tsv.
 *
 * Wiring only — every decision the import makes lives in utils/salesImport/* and
 * utils/importFileParser, so it can be unit tested. The write path is the ordinary
 * `saveNewSale`, which is what makes an imported sale identical to one rung up at the
 * till (stock movements, transaction, client totals, dailyStats, Supabase dual-write).
 *
 * Steps: upload → map columns → settings → map clients → preview → import → report.
 * Nothing is written before the user confirms the preview.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Platform, ScrollView, StyleSheet, View } from 'react-native'
import { Button, CheckBox, Icon, Radio, Spinner, Text, Toggle, useTheme } from '@ui-kitten/components'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import * as DocumentPicker from 'expo-document-picker'
import { Row, Table } from 'react-native-table-component'

import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { HintView } from '../../components/HintView'
import {
  currencyFormatterAtom,
  paymentMethodsDictAtom,
  priceTypesAtom,
  productsDictAtom,
  storesDictAtom,
  userRoleAtom,
} from '../../global/AtomsProvider'
import { addNewClient, findClientByPhone, saveNewSale } from '../../global/Firebase/firebase'
import { getClientById } from '../../global/Firebase/clients'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { exportToCSV } from '../../utils/exportUtils'
import {
  IMPORT_DOCUMENT_PICKER_TYPES,
  IMPORT_DROPZONE_ACCEPT,
  MalformedImportFileError,
  UnsupportedImportFileError,
  readImportFileFromUri,
  readImportFileFromWeb,
} from '../../utils/importFileReader'
import { parseImportDate, parseSalesRows } from '../../utils/salesImport/parseRows'
import {
  buildProductIndex,
  collectClientKeys,
  resolvePaymentMethod as matchPaymentMethod,
  resolveProductsForRows,
} from '../../utils/salesImport/resolve'
import { groupSalesOrders } from '../../utils/salesImport/groupOrders'
import { orderTotal } from '../../utils/salesImport/buildSaleInfo'
import {
  clientForOrder,
  createMissingClients,
  formatPhoneForDisplay,
  resolveClientsByPhone,
} from '../../utils/salesImport/clientResolution'
import { runSalesImport } from '../../utils/salesImport/runImport'
import { createImportBatchId } from '../../utils/salesImport/batchId'

// Conditional import for web only (same pattern as ExportImportScreen)
const ReactDropzone = Platform.select({
  web: () => require('react-dropzone'),
  default: () => null,
})?.()

const STEPS = {
  UPLOAD: 'upload',
  MAPPING: 'mapping',
  SETTINGS: 'settings',
  CLIENTS: 'clients',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  DONE: 'done',
}

const POST_PAID_OPTION_ID = '__postPaid__'
/** How many orders the preview lists before collapsing into "…and N more". */
const PREVIEW_ORDER_LIMIT = 10

export default function SalesImportScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [context] = useContext(UserContext)
  const db = context.db
  const productsDict = useAtomValue(productsDictAtom)
  const paymentMethodsDict = useAtomValue(paymentMethodsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const priceTypes = useAtomValue(priceTypesAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)

  const [step, setStep] = useState(STEPS.UPLOAD)
  const [sheet, setSheet] = useState(null)
  const [importedColumns, setImportedColumns] = useState([])
  const [columnMapping, setColumnMapping] = useState({})
  const [store, setStore] = useState(context.user.store?.id)
  const [joinByClientAndDate, setJoinByClientAndDate] = useState(true)
  const [defaultPaymentOption, setDefaultPaymentOption] = useState(null)
  const [clientResolutions, setClientResolutions] = useState({})
  const [isResolvingClients, setIsResolvingClients] = useState(false)
  const [skipInvalidRows, setSkipInvalidRows] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const [report, setReport] = useState(null)
  // Errors render inline rather than through RN's Alert: react-native-web implements
  // Alert.alert as an empty function, so on web — the platform people actually import
  // spreadsheets on — every Alert here would fail silently and the button would just
  // appear to do nothing. Same pattern NewSaleScreen uses for its save errors.
  const [formError, setFormError] = useState(null)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton onPress={() => navigation.navigate('Home')} iconName='arrow-back-outline' />
        ),
      })
    }
    if (!userRole?.permissions?.['exportImport'] && context.user?.role !== 'admin') {
      navigation.navigate('Home')
    }
  }, [])

  // ---------------------------------------------------------------- column mapping

  const salesFields = useMemo(
    () => [
      { label: t('salesImport.article'), value: 'article', required: true, aliases: ['артикул', 'article', 'sku', 'штрихкод', 'barcode', 'код'] },
      { label: t('salesImport.quantity'), value: 'quantity', required: true, aliases: ['штук', 'количество', 'кол-во', 'quantity', 'qty', 'amount'] },
      { label: t('salesImport.date'), value: 'date', required: true, aliases: ['дата', 'date'] },
      { label: t('salesImport.client'), value: 'client', required: false, aliases: ['клиент', 'client', 'телефон', 'phone', 'номер'] },
      { label: t('salesImport.paymentMethod'), value: 'paymentMethod', required: false, aliases: ['оплата', 'способ оплаты', 'payment', 'payment method'] },
    ],
    [t]
  )

  const autoMapColumns = (loadedSheet) => {
    const headerRow = loadedSheet.rows[0] || []
    const mapping = {}
    headerRow.forEach((header, index) => {
      const normalized = String(header ?? '').trim().toLowerCase()
      if (!normalized) return
      salesFields.forEach((field) => {
        if (mapping[field.value]) return
        const matches =
          field.label.toLowerCase() === normalized || field.aliases.some((alias) => alias === normalized)
        if (matches) mapping[field.value] = { name: String(header), text: String(header), index }
      })
    })
    setImportedColumns([
      ...headerRow.map((column, index) => ({
        name: String(column ?? ''),
        text: String(column ?? `#${index + 1}`),
        index,
      })),
      { index: -1, name: t('salesImport.ignore'), text: t('salesImport.ignore') },
    ])
    setColumnMapping(mapping)
  }

  const handleColumnMapping = (field, importedColumn) => {
    setColumnMapping((previous) => ({ ...previous, [field.value]: importedColumn }))
  }

  const mappingForParser = useMemo(
    () => ({
      article: columnMapping.article?.index ?? null,
      quantity: columnMapping.quantity?.index ?? null,
      date: columnMapping.date?.index ?? null,
      client: columnMapping.client?.index ?? null,
      paymentMethod: columnMapping.paymentMethod?.index ?? null,
    }),
    [columnMapping]
  )

  /** `{year, month, day, …}` → `10.07.2026`, with the time only when the file gave one. */
  const formatImportDate = (date) => {
    const pad = (n) => String(n).padStart(2, '0')
    const day = `${pad(date.day)}.${pad(date.month)}.${date.year}`
    return date.hasTime ? `${day} ${pad(date.hour)}:${pad(date.minute)}` : day
  }

  // ---------------------------------------------------------------- file handling

  const loadSheet = async (loadedSheet) => {
    if (!loadedSheet || loadedSheet.rows.length < 2) {
      setFormError(t('salesImport.emptyFile'))
      return
    }
    setFormError(null)
    setSheet(loadedSheet)
    autoMapColumns(loadedSheet)
    setStep(STEPS.MAPPING)
  }

  /** An unsupported extension is a user mistake, not a crash — say so in those words. */
  const reportFileError = (error) => {
    console.error('Error reading import file:', error)
    setFormError(
      error instanceof UnsupportedImportFileError
        ? t('salesImport.unsupportedFile')
        : t('salesImport.readError', {
          error: error instanceof MalformedImportFileError ? t('importFileMalformedQuotes', { row: error.row }) : error.message,
        })
    )
  }

  const handleFileSelect = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: IMPORT_DOCUMENT_PICKER_TYPES,
        copyToCacheDirectory: true,
      })
      if (result.canceled) return
      const asset = result.assets[0]
      await loadSheet(await readImportFileFromUri(asset.uri, asset.name))
    } catch (error) {
      reportFileError(error)
    }
  }

  const onDrop = useCallback(async (acceptedFiles) => {
    // react-dropzone hands back an EMPTY list when the file fails its `accept` filter —
    // dropping a .pdf lands here with nothing. Reading acceptedFiles[0] blindly surfaced a
    // raw "Cannot read properties of undefined (reading 'name')" to the user.
    if (!acceptedFiles || acceptedFiles.length === 0) {
      setFormError(t('salesImport.unsupportedFile'))
      return
    }
    try {
      await loadSheet(await readImportFileFromWeb(acceptedFiles[0]))
    } catch (error) {
      reportFileError(error)
    }
  }, [salesFields, t])

  const { getRootProps, getInputProps, isDragActive } =
    Platform.OS === 'web'
      ? ReactDropzone.useDropzone({ onDrop, accept: IMPORT_DROPZONE_ACCEPT, multiple: false })
      : { getRootProps: null, getInputProps: null, isDragActive: false }

  // ---------------------------------------------------------------- parsing & grouping

  const productIndex = useMemo(() => buildProductIndex(productsDict), [productsDict])

  const parsed = useMemo(() => {
    if (!sheet || mappingForParser.article === null) return null
    const { rows, errors } = parseSalesRows(sheet, mappingForParser)
    const resolved = resolveProductsForRows(rows, productIndex)
    return {
      rows: resolved.rows,
      errors: [...errors, ...resolved.errors].sort((a, b) => a.rowNumber - b.rowNumber),
      unknownArticles: resolved.unknownArticles,
    }
  }, [sheet, mappingForParser, productIndex])

  const orders = useMemo(
    () => (parsed ? groupSalesOrders(parsed.rows, { joinByClientAndDate }) : []),
    [parsed, joinByClientAndDate]
  )

  const paymentOptions = useMemo(
    () => [
      ...Object.values(paymentMethodsDict || {}).map((method) => ({
        ...method,
        text: method.name || method.text,
      })),
      { id: POST_PAID_OPTION_ID, text: t('salesImport.postPaid'), name: t('salesImport.postPaid') },
    ],
    [paymentMethodsDict, t]
  )

  const resolveOrderPaymentMethod = useCallback(
    (paymentMethodKey) => {
      const fromCell = matchPaymentMethod(paymentMethodsDict, paymentMethodKey)
      if (fromCell) return fromCell
      if (!defaultPaymentOption || defaultPaymentOption.id === POST_PAID_OPTION_ID) return null
      return defaultPaymentOption
    },
    [paymentMethodsDict, defaultPaymentOption]
  )

  /**
   * What the import will charge in total. This is the confirmation gate for money landing
   * in the books, so it is computed with the same helper `buildSaleInfo` uses — the figure
   * shown here is the figure that gets written.
   */
  const previewTotal = useMemo(
    () => orders.reduce((sum, order) => sum + orderTotal(order, store, priceTypes), 0),
    [orders, store, priceTypes]
  )

  /** Total the import would sell per product, to warn about stock going negative. */
  const negativeStockWarnings = useMemo(() => {
    const totals = new Map()
    orders.forEach((order) => {
      order.items.forEach((item) => {
        const id = item.product?.id
        if (!id) return
        const entry = totals.get(id) || { product: item.product, quantity: 0 }
        entry.quantity += item.quantity
        totals.set(id, entry)
      })
    })
    const warnings = []
    totals.forEach(({ product, quantity }, id) => {
      // Composite products sold from ingredients have no stock of their own.
      if (product.isComposite && !product.manufacturedBeforeSale) return
      const inStock = product.storeStock?.[store] ?? 0
      if (inStock - quantity < 0) {
        // Keyed by id, not name — two products may legitimately share a name.
        warnings.push({ id, name: product.name, inStock, quantity, resulting: inStock - quantity })
      }
    })
    return warnings
  }, [orders, store])

  // ---------------------------------------------------------------- client resolution

  /** The I/O utils/salesImport/clientResolution needs, bound to the active read source. */
  const clientDeps = useMemo(
    () => ({
      findByPhone: (phone) => findClientByPhone(db, phone),
      loadById: (firestoreId) => getClientById(db, firestoreId),
      create: async (phone) => {
        const info = {
          organizationId: context.user.organizationId,
          // The file carries no name, so the phone IS the name — but in readable form.
          displayName: formatPhoneForDisplay(phone),
          name: formatPhoneForDisplay(phone),
          phoneNumber: phone,
          phone,
          bonusValue: 0,
          punchCardValue: 0,
          registrationDate: new Date(),
          registrationSource: store,
          dailyStatID: getDailyStatID(store, new Date()),
        }
        const ref = await addNewClient(db, info)
        return { id: ref.id, ...info }
      },
      // Persist each resolution as it happens, so a failure partway cannot make the
      // clients already created look uncreated (a retry would create them twice).
      onResolved: (phone, resolution) =>
        setClientResolutions((previous) => ({ ...previous, [phone]: resolution })),
    }),
    [db, context.user.organizationId, store]
  )

  const resolveClients = async () => {
    if (!parsed) return
    const keys = collectClientKeys(parsed.rows)
    if (keys.length === 0) {
      setClientResolutions({})
      setStep(STEPS.PREVIEW)
      return
    }

    setIsResolvingClients(true)
    setStep(STEPS.CLIENTS)
    setClientResolutions(
      await resolveClientsByPhone(keys, clientDeps, (phone, error) =>
        console.error('Client lookup failed for', phone, error)
      )
    )
    setIsResolvingClients(false)
  }

  const setClientAction = (key, action) => {
    setClientResolutions((previous) => ({
      ...previous,
      [key]: { ...previous[key], action, failed: false },
    }))
  }

  // ---------------------------------------------------------------- import

  const startImport = async () => {
    setStep(STEPS.IMPORTING)
    setProgress({ completed: 0, total: orders.length })
    try {
      const resolutions = await createMissingClients(clientResolutions, clientDeps)
      const result = await runSalesImport({
        orders,
        context: {
          store,
          organizationId: context.user.organizationId,
          cashierUID: context.user.uid,
          importBatchId: createImportBatchId(),
          priceTypes,
        },
        resolveClient: (clientKey) => clientForOrder(resolutions, clientKey),
        resolvePaymentMethod: resolveOrderPaymentMethod,
        saveSale: (cartInfo) => saveNewSale(db, cartInfo),
        onProgress: (completed, total) => setProgress({ completed, total }),
      })
      setReport(result)
      setStep(STEPS.DONE)
    } catch (error) {
      console.error('Sales import failed:', error)
      setFormError(t('salesImport.importError', { error: error.message }))
      setStep(STEPS.PREVIEW)
    }
  }

  const downloadIssuesReport = async () => {
    const rows = [
      ...(parsed?.errors || []).map((error) => ({
        [t('salesImport.reportRow')]: error.rowNumber,
        [t('salesImport.reportStatus')]: t('salesImport.reportSkipped'),
        [t('salesImport.reportReason')]: t(`salesImport.error.${error.code}`),
        [t('salesImport.reportValue')]: error.value,
      })),
      ...(report?.failures || []).flatMap((failure) =>
        failure.rowNumbers.map((rowNumber) => ({
          [t('salesImport.reportRow')]: rowNumber,
          [t('salesImport.reportStatus')]: t('salesImport.reportFailed'),
          [t('salesImport.reportReason')]: failure.message,
          [t('salesImport.reportValue')]: '',
        }))
      ),
    ]
    if (rows.length === 0) return
    await exportToCSV(rows, `sales-import-issues-${Date.now()}`)
  }

  const restart = () => {
    setSheet(null)
    setImportedColumns([])
    setColumnMapping({})
    setClientResolutions({})
    setSkipInvalidRows(false)
    setProgress({ completed: 0, total: 0 })
    setReport(null)
    setStep(STEPS.UPLOAD)
  }

  // ---------------------------------------------------------------- render helpers

  const UploadIcon = (props) => (
    <Icon
      {...props}
      name='upload-outline'
      width={24}
      height={24}
      fill={isDragActive ? theme['color-info-500'] : theme['text-hint-color']}
    />
  )

  const renderUploadStep = () => {
    if (Platform.OS === 'web') {
      return (
        <div
          {...getRootProps()}
          style={{
            ...styles.dropzone,
            borderColor: isDragActive ? theme['color-info-500'] : theme['border-basic-color-3'],
            backgroundColor: isDragActive
              ? theme['color-info-transparent-100']
              : theme['background-basic-color-2'],
          }}
        >
          <input {...getInputProps()} />
          <View style={styles.uploadButton}>
            <UploadIcon />
            <Text
              style={{
                ...styles.uploadButtonText,
                color: isDragActive ? theme['color-info-500'] : theme['text-hint-color'],
              }}
            >
              {isDragActive ? t('salesImport.dropFileHere') : t('salesImport.uploadSales')}
            </Text>
          </View>
          <Text style={[styles.dropzoneText, { color: theme['text-hint-color'] }]}>
            {t('salesImport.dragAndDropHint')}
          </Text>
        </div>
      )
    }
    return (
      <Button onPress={handleFileSelect} style={{ marginBottom: 20 }}>
        {t('salesImport.uploadSales')}
      </Button>
    )
  }

  /** A floor width per column — otherwise a 13-digit barcode wraps mid-number. */
  const previewColumnWidths = useMemo(
    () => new Array(sheet?.rows?.[0]?.length || 0).fill(170),
    [sheet]
  )

  const renderPreviewTable = () => (
    <ScrollView
      horizontal
      style={styles.tableScroll}
      contentContainerStyle={[styles.tableContainer, { backgroundColor: theme['background-basic-color-1'] }]}
    >
      <Table style={styles.tableView} borderStyle={{ borderWidth: 0 }}>
        {sheet.rows.slice(0, 6).map((rowData, index) => (
          <Row
            key={index}
            // Date cells arrive as Excel serials (46213). Showing that raw makes the one
            // column the user most needs to check unreadable, so render it as a date.
            data={rowData.map((cell, column) => {
              if (cell === null) return ''
              if (index > 0 && column === mappingForParser.date) {
                const parsed = parseImportDate(cell, sheet.date1904)
                if (parsed) return formatImportDate(parsed)
              }
              return String(cell)
            })}
            widthArr={previewColumnWidths}
            style={styles.row}
            textStyle={index === 0 ? styles.headerText : styles.text}
          />
        ))}
      </Table>
    </ScrollView>
  )

  const missingRequiredColumns = salesFields
    .filter((field) => field.required)
    .filter((field) => !columnMapping[field.value] || columnMapping[field.value].index === -1)

  // ---------------------------------------------------------------- render

  return (
    <ScrollView style={{ padding: 20 }} contentContainerStyle={styles.pageContent}>
      <Text category='h3' style={{ marginBottom: 5 }}>
        {t('salesImport.title')}
      </Text>
      {step === STEPS.UPLOAD && (
        <HintView text={t('salesImport.hint')} iconName='alert-circle-outline' style={{ marginBottom: 10 }} />
      )}

      {formError && (
        <Text status='danger' testID='sales-import-error' style={{ marginBottom: 10 }}>
          {formError}
        </Text>
      )}

      {step === STEPS.UPLOAD && renderUploadStep()}

      {step === STEPS.MAPPING && sheet && (
        <View style={{ width: '100%' }}>
          <Text category='h6' style={{ marginBottom: 10 }}>
            {t('salesImport.previewImport')}
          </Text>
          {renderPreviewTable()}
          <Text style={styles.text}>{t('salesImport.totalRows', { count: sheet.rows.length - 1 })}</Text>

          <Text category='h6' style={{ marginTop: 20, marginBottom: 10 }}>
            {t('salesImport.mapColumns')}
          </Text>
          {salesFields.map((field) => (
            <View key={field.value} style={styles.mappingRow}>
              <Text style={styles.mappingLabel}>{field.label}{field.required ? ' *' : ''}</Text>
              <PlatformDependantSelect
                data={importedColumns}
                style={styles.select}
                placeholder={t('salesImport.selectField')}
                selectedOption={columnMapping[field.value]}
                onSelect={(newValue) => handleColumnMapping(field, newValue)}
              />
            </View>
          ))}

          <Button
            style={{ marginTop: 20 }}
            onPress={() => {
              if (missingRequiredColumns.length > 0) {
                setFormError(
                  t('salesImport.requiredColumnsMissing', {
                    columns: missingRequiredColumns.map((field) => field.label).join(', '),
                  })
                )
                return
              }
              setFormError(null)
              setStep(STEPS.SETTINGS)
            }}
          >
            {t('salesImport.next')}
          </Button>
        </View>
      )}

      {step === STEPS.SETTINGS && (
        <View style={{ width: '100%' }}>
          <Text category='h6' style={{ marginBottom: 10 }}>{t('salesImport.store')}</Text>
          <StoreChooserHorizontal store={store} showAll={false} onChange={setStore} />

          <Text category='h6' style={{ marginTop: 20, marginBottom: 5 }}>
            {t('salesImport.grouping')}
          </Text>
          <HintView text={t('salesImport.groupingHint')} iconName='alert-circle-outline' style={{ marginBottom: 10 }} />
          <Toggle checked={joinByClientAndDate} onChange={setJoinByClientAndDate}>
            {t('salesImport.joinByClientAndDate')}
          </Toggle>

          <Text category='h6' style={{ marginTop: 20, marginBottom: 5 }}>
            {t('salesImport.defaultPaymentMethod')}
          </Text>
          <HintView
            text={t('salesImport.defaultPaymentMethodHint')}
            iconName='alert-circle-outline'
            style={{ marginBottom: 10 }}
          />
          <PlatformDependantSelect
            data={paymentOptions}
            style={{ width: 260 }}
            placeholder={t('salesImport.selectPaymentMethod')}
            selectedOption={defaultPaymentOption}
            onSelect={setDefaultPaymentOption}
          />

          <Button
            style={{ marginTop: 20 }}
            onPress={() => {
              if (!defaultPaymentOption) {
                setFormError(t('salesImport.paymentMethodRequired'))
                return
              }
              if (!store || !storesDict[store]) {
                setFormError(t('salesImport.storeRequired'))
                return
              }
              setFormError(null)
              resolveClients()
            }}
          >
            {t('salesImport.next')}
          </Button>
        </View>
      )}

      {step === STEPS.CLIENTS && (
        <View style={{ width: '100%' }}>
          <Text category='h6' style={{ marginBottom: 10 }}>{t('salesImport.mapClients')}</Text>
          <HintView text={t('salesImport.mapClientsHint')} iconName='alert-circle-outline' style={{ marginBottom: 10 }} />
          {isResolvingClients ? (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Spinner size='small' style={{ marginRight: 10 }} />
              <Text>{t('salesImport.searchingClients')}</Text>
            </View>
          ) : (
            <View style={{ width: '100%' }}>
              {Object.entries(clientResolutions).map(([key, resolution]) => (
                <View key={key} style={styles.clientRow}>
                  <Text category='s1'>{formatPhoneForDisplay(key)}</Text>
                  {resolution.action === 'existing' && resolution.client ? (
                    <Text appearance='hint'>
                      {t('salesImport.clientFound', {
                        name: resolution.client.displayName || resolution.client.name || key,
                      })}
                    </Text>
                  ) : (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                      {resolution.failed && (
                        <Text status='danger' style={{ marginRight: 10 }}>
                          {t('salesImport.clientLookupFailed')}
                        </Text>
                      )}
                      <Radio
                        style={{ marginRight: 15 }}
                        checked={resolution.action === 'create'}
                        onChange={() => setClientAction(key, 'create')}
                      >
                        {t('salesImport.createClient')}
                      </Radio>
                      <Radio
                        checked={resolution.action === 'skip'}
                        onChange={() => setClientAction(key, 'skip')}
                      >
                        {t('salesImport.withoutClient')}
                      </Radio>
                    </View>
                  )}
                </View>
              ))}
              <Button style={{ marginTop: 20 }} onPress={() => setStep(STEPS.PREVIEW)}>
                {t('salesImport.next')}
              </Button>
            </View>
          )}
        </View>
      )}

      {step === STEPS.PREVIEW && parsed && (
        <View style={{ width: '100%' }}>
          <Text category='h6' style={{ marginBottom: 10 }}>{t('salesImport.summary')}</Text>
          <View style={[styles.summaryCard, { backgroundColor: theme['background-basic-color-1'] }]}>
            <Text category='h5'>{t('salesImport.summaryOrders', { count: orders.length })}</Text>
            <Text category='s1' testID='sales-import-total' style={{ marginTop: 2 }}>
              {t('salesImport.summaryTotal', { total: currencyFormatter.format(previewTotal) })}
            </Text>
            <Text appearance='hint' style={{ marginTop: 6 }}>
              {t('salesImport.summaryRows', { count: parsed.rows.length })} ·{' '}
              {t('salesImport.summaryClients', {
                count: Object.values(clientResolutions).filter((r) => r.action !== 'skip').length,
              })}
            </Text>
          </View>

          {/* The counts alone ask the user to approve money on trust. Show the orders. */}
          <Text category='s1' style={{ marginTop: 15, marginBottom: 5 }}>
            {t('salesImport.ordersPreview')}
          </Text>
          {orders.slice(0, PREVIEW_ORDER_LIMIT).map((order) => (
            <View key={order.key} style={styles.orderRow}>
              <Text>
                {formatImportDate(order.date)}
                {'  ·  '}
                {order.clientKey
                  ? formatPhoneForDisplay(order.clientKey)
                  : t('salesImport.withoutClient')}
                {'  ·  '}
                {currencyFormatter.format(orderTotal(order, store, priceTypes))}
              </Text>
              <Text appearance='hint' style={styles.text}>
                {order.items.map((item) => `${item.product.name} × ${item.quantity}`).join(', ')}
              </Text>
            </View>
          ))}
          {orders.length > PREVIEW_ORDER_LIMIT && (
            <Text appearance='hint' style={styles.text}>
              {t('salesImport.andMore', { count: orders.length - PREVIEW_ORDER_LIMIT })}
            </Text>
          )}

          {negativeStockWarnings.length > 0 && (
            <View style={{ marginTop: 15 }}>
              <Text category='s1' status='warning'>{t('salesImport.negativeStockWarning')}</Text>
              {negativeStockWarnings.slice(0, 20).map((warning) => (
                <Text key={warning.id} style={styles.text} appearance='hint'>
                  {t('salesImport.negativeStockRow', {
                    name: warning.name,
                    inStock: warning.inStock,
                    quantity: warning.quantity,
                    resulting: warning.resulting,
                  })}
                </Text>
              ))}
            </View>
          )}

          {parsed.errors.length > 0 && (
            <View style={{ marginTop: 15 }}>
              <Text category='s1' status='danger'>
                {t('salesImport.invalidRows', { count: parsed.errors.length })}
              </Text>
              {parsed.errors.slice(0, 20).map((error) => (
                <Text key={`${error.rowNumber}-${error.code}`} style={styles.text} appearance='hint'>
                  {error.value
                    ? t('salesImport.invalidRowWithValue', {
                        row: error.rowNumber,
                        reason: t(`salesImport.error.${error.code}`),
                        value: error.value,
                      })
                    : t('salesImport.invalidRow', {
                        row: error.rowNumber,
                        reason: t(`salesImport.error.${error.code}`),
                      })}
                </Text>
              ))}
              {parsed.errors.length > 20 && (
                <Text style={styles.text} appearance='hint'>
                  {t('salesImport.andMore', { count: parsed.errors.length - 20 })}
                </Text>
              )}
              <Button
                appearance='ghost'
                style={{ alignSelf: 'flex-start', marginTop: 5 }}
                onPress={downloadIssuesReport}
              >
                {t('salesImport.downloadIssues')}
              </Button>
              <CheckBox
                style={{ marginTop: 10 }}
                checked={skipInvalidRows}
                onChange={setSkipInvalidRows}
              >
                {t('salesImport.skipInvalidRows', { count: parsed.errors.length })}
              </CheckBox>
            </View>
          )}

          <Button
            testID='sales-import-submit'
            accessibilityRole='button'
            style={{ marginTop: 20 }}
            disabled={orders.length === 0 || (parsed.errors.length > 0 && !skipInvalidRows)}
            onPress={startImport}
          >
            {t('salesImport.import', { count: orders.length })}
          </Button>
        </View>
      )}

      {step === STEPS.IMPORTING && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 20 }}>
          <Spinner size='small' style={{ marginRight: 10 }} />
          <Text>
            {t('salesImport.importProgress', { count: progress.completed, totalCount: progress.total })}
          </Text>
        </View>
      )}

      {step === STEPS.DONE && report && (
        <View style={{ width: '100%' }}>
          <Text category='h6' style={{ marginBottom: 10 }}>{t('salesImport.done')}</Text>
          <Text style={styles.text}>{t('salesImport.doneOrders', { count: report.createdOrders })}</Text>
          <Text style={styles.text}>{t('salesImport.doneRows', { count: report.createdRows })}</Text>
          {report.failures.length > 0 && (
            <View style={{ marginTop: 10 }}>
              <Text category='s1' status='danger'>
                {t('salesImport.failedOrders', { count: report.failures.length })}
              </Text>
              {report.failures.slice(0, 20).map((failure) => (
                <Text key={failure.rowNumbers.join(',')} style={styles.text} appearance='hint'>
                  {t('salesImport.failedOrder', {
                    rows: failure.rowNumbers.join(', '),
                    reason: failure.message,
                  })}
                </Text>
              ))}
            </View>
          )}
          {(report.failures.length > 0 || (parsed?.errors.length || 0) > 0) && (
            <Button appearance='ghost' style={{ alignSelf: 'flex-start' }} onPress={downloadIssuesReport}>
              {t('salesImport.downloadIssues')}
            </Button>
          )}
          <Button style={{ marginTop: 20 }} onPress={restart}>
            {t('salesImport.importAnotherFile')}
          </Button>
        </View>
      )}

      <View style={{ height: 50 }} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  // The wizard is a form, not a dashboard — let it breathe but stop it stretching across
  // a 1400px desktop window.
  pageContent: { alignItems: 'stretch', maxWidth: 820, width: '100%' },
  row: {
    minHeight: 48,
    borderBottomWidth: 1,
    borderBottomColor: 'gray',
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10,
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10,
  },
  // Fixed label width so the mapping selects line up instead of stepping raggedly with
  // each label's length.
  mappingRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  mappingLabel: { width: 150 },
  summaryCard: {
    alignSelf: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    minWidth: 260,
  },
  orderRow: { marginBottom: 10 },
  clientRow: { marginBottom: 15 },
  select: { width: 200, marginLeft: 10 },
  dropzone: {
    alignSelf: 'stretch',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  uploadButtonText: {
    marginLeft: 10,
    fontSize: 16,
  },
  dropzoneText: {
    marginTop: 10,
    fontSize: 14,
  },
  tableView: {
    marginBottom: 10,
    width: '100%',
  },
  tableContainer: {
    flexGrow: 0,
    marginBottom: 10,
    borderRadius: 10,
    paddingBottom: 10,
    paddingHorizontal: 10,
  },
  tableScroll: {
    width: '100%',
  },
})
